var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { debounce } from 'lodash';
export const GoogleMapsApiKeyContext = createContext(null);
const apisByKey = {};
export function useGoogleMapsApi(props) {
    const apiKey = useContext(GoogleMapsApiKeyContext);
    const apiKeyRef = useRef(null);
    apiKeyRef.current = apiKey;
    if (!apiKey) {
        console.warn('Google Maps API Key missing, please use GoogleMapsApiKeyContext.Provider');
    }
    if (apiKey && !apisByKey[apiKey]) {
        const loader = new Loader({ apiKey, version: 'weekly' });
        apisByKey[apiKey] = Promise.all([
            loader.importLibrary('core'),
            loader.importLibrary('places'),
            loader.importLibrary('geocoding'),
        ]).then(([core, places, geocoding]) => ({
            LatLng: core.LatLng,
            Place: places.Place,
            sessionToken: new places.AutocompleteSessionToken(),
            autocomplete: new places.AutocompleteService(),
            geocoder: new geocoding.Geocoder(),
        }));
    }
    function getApis() {
        return __awaiter(this, void 0, void 0, function* () {
            const apiKey = apiKeyRef.current;
            const apisPromise = apiKey ? apisByKey[apiKey] : Promise.resolve(null);
            const apis = yield apisPromise;
            if (!apis) {
                console.warn('Google APIs not loaded - did you forget to provide the API key?');
                throw new Error('Not able to use Google APIs');
            }
            return apis;
        });
    }
    const getPlacePredictions = useMemo(() => {
        var _a;
        function getPlacePredictionsRaw(request, callback) {
            return __awaiter(this, void 0, void 0, function* () {
                const apis = yield getApis();
                return apis.autocomplete.getPlacePredictions(Object.assign(Object.assign({}, request), { sessionToken: apis.sessionToken }), callback);
            });
        }
        return debounce(getPlacePredictionsRaw, (_a = props === null || props === void 0 ? void 0 : props.debounceMs) !== null && _a !== void 0 ? _a : 400);
    }, []);
    const geocode = useMemo(() => {
        var _a;
        function geocodeRaw(request, callback) {
            return __awaiter(this, void 0, void 0, function* () {
                const apis = yield getApis();
                return apis.geocoder.geocode(request, callback);
            });
        }
        return debounce(geocodeRaw, (_a = props === null || props === void 0 ? void 0 : props.debounceMs) !== null && _a !== void 0 ? _a : 400);
    }, []);
    const getCountry = useCallback((coords) => __awaiter(this, void 0, void 0, function* () {
        const apis = yield getApis();
        const latLng = new apis.LatLng(coords.latitude, coords.longitude);
        const response = yield apis.geocoder.geocode({ location: latLng });
        const country = response
            .results
            .flatMap(result => result.address_components)
            .find(component => component.types.includes('country'));
        return country !== null && country !== void 0 ? country : null;
    }), []);
    const getCoordinatesForAddress = useCallback((address) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const apis = yield getApis();
        const fields = [
            address.address1,
            address.city,
            address.state,
            address.country,
        ];
        const text = fields.filter(it => it).join(', ');
        const response = yield apis.Place.searchByText({
            textQuery: text,
            fields: ['location'],
            maxResultCount: 1,
        });
        const place = (_a = response.places[0]) !== null && _a !== void 0 ? _a : null;
        return (_b = place === null || place === void 0 ? void 0 : place.location) !== null && _b !== void 0 ? _b : null;
    }), []);
    return {
        getPlacePredictions,
        getCountry,
        geocode,
        getCoordinatesForAddress
    };
}
//# sourceMappingURL=google-maps-api.js.map